
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardBody, Col, Row, Button, Input, Label, Spinner } from 'reactstrap';
import { Download, Truck, MapPin, Printer, FileText, Search, Calendar } from 'react-feather';
import { useReactToPrint } from 'react-to-print';
import { apiPost } from '../../../Utils';
import Breadcrumbs from '../../../CommonElements/Breadcrumbs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../../../Context/AuthContext';
import { useConfig } from '../../../Context/ConfigContext';
import DataTable from 'react-data-table-component';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

interface Branch {
    id: string;
    branch_name: string;
}

interface Vehicle {
    id: string;
    registration_number: string;
}

interface LPORecord {
    id: string;
    lpo_number: string;
    branch_name: string;
    registration_number: string;
    lpo_date: string;
    vendor_name: string;
    labour_vendor_name: string;
    lpo_type: string;
    amount: number;
    status: string;
}

const LPOReport = () => {
    const { hasPermission } = useAuth();
    const { config } = useConfig();
    const componentRef = useRef(null);
    const printRef = useRef(null);

    // Dropdown Data
    const [branches, setBranches] = useState<Branch[]>([]);
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);

    // Filters State
    const [branchId, setBranchId] = useState('');
    const [vehicleId, setVehicleId] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);

    const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Data State
    const [reportData, setReportData] = useState<LPORecord[]>([]);
    const [loading, setLoading] = useState(false);

    // Sorting State
    const [sortBy, setSortBy] = useState('lpo_date');
    const [sortOrder, setSortOrder] = useState('desc');

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [perPage, setPerPage] = useState(10);

    const [printData, setPrintData] = useState<LPORecord[]>([]);
    const prevSearch = useRef(searchTerm);

    // Columns
    const columns = [
        {
            name: <div>Sl.No</div>,
            cell: (_: any, index: number) => (currentPage - 1) * perPage + index + 1,
            width: '70px',
            center: true,
        },
        {
            name: <div>LPO No.</div>,
            selector: (row: LPORecord) => row.lpo_number,
            sortable: true,
            sortField: 'lpo_number',
            wrap: true
        },

        {
            name: <div>Vehicle</div>,
            selector: (row: LPORecord) => row.registration_number,
            sortable: true,
            cell: (row: any) => (
                <span className="text-primary">
                    {row.registration_number}
                    {row.vehicle_deleted_at && (
                        <span className="text-danger"> (Deleted)</span>
                    )}
                </span>

            ),
            sortField: 'registration_number',
            wrap: true
        },
        {
            name: <div>Branch</div>,
            selector: (row: LPORecord) => row.branch_name,
            sortable: true,
            sortField: 'branch_name',
            cell: (row: any) => (
                <span>
                    {row.branch_name}
                    {row.branch_deleted_at && (
                        <span className="text-danger"> (Deleted)</span>
                    )}
                </span>
            ),
            wrap: true
        },
        {
            name: <div>LPO Date</div>,
            selector: (row: LPORecord) => row.lpo_date,
            sortable: true,
            cell: (row: LPORecord) => row.lpo_date ? new Date(row.lpo_date).toLocaleDateString('en-GB') : '-',
            sortField: 'lpo_date',
            wrap: true
        },
        {
            name: <div>Vendor</div>,
            selector: (row: LPORecord) => row.vendor_name,
            sortable: true,
            sortField: 'vendor_name',
            wrap: true,
            cell: (row: LPORecord) => {
                const type = row.lpo_type;
                const pVendor = row.vendor_name;
                const lVendor = row.labour_vendor_name || row.vendor_name;

                if (type === 'Same Hybrid') return <div><strong>Parts & Labour:</strong> {pVendor}</div>;
                if (type === 'Diff Hybrid') return <div><strong>Parts:</strong> {pVendor}<br /><strong>Labour:</strong> {lVendor}</div>;
                if (type === 'Labour') return <div><strong>Labour:</strong> {lVendor}</div>;
                return <div><strong>Parts:</strong> {pVendor}</div>;
            }
        },
        {
            name: <div>Amount ({config.currency})</div>,
            selector: (row: LPORecord) => row.amount,
            sortable: true,
            cell: (row: LPORecord) => <span className="fw-bold text-success">{row.amount ? Number(row.amount).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'}</span>,
            sortField: 'amount',
        }
    ];

    // Fetch Dropdown Data
    useEffect(() => {
        apiPost("branches/list", {})
            .then((res: any) => {
                if (res && res.data) {
                    setBranches(Array.isArray(res.data) ? res.data : []);
                } else {
                    setBranches(Array.isArray(res) ? res : []);
                }
            })
            .catch(err => console.error('Failed to fetch branches:', err));
    }, []);

    // Filter Vehicles when Branch Changes
    useEffect(() => {
        if (!branchId) {
            setVehicles([]);
            setVehicleId('');
            return;
        }

        apiPost("vehicles/list", { branch_id: branchId })
            .then((res: any) => {
                if (res && res.data) {
                    setVehicles(Array.isArray(res.data) ? res.data : []);
                } else {
                    setVehicles(Array.isArray(res) ? res : []);
                }
            })
            .catch(() => setVehicles([]));

        setVehicleId('');
    }, [branchId]);

    // Main Data Fetch
    const fetchReport = () => {
        setLoading(true);
        const payload: any = {
            page: currentPage,
            per_page: perPage,
            sort_by: sortBy,
            sort_order: sortOrder,
            search: searchTerm,
            from_date: startDate ? formatDate(startDate) : '',
            to_date: endDate ? formatDate(endDate) : ''
        };
        if (branchId) payload.branch_id = branchId;
        if (vehicleId) payload.vehicle_id = vehicleId;

        apiPost("reports/lpo", payload)
            .then((res: any) => {
                if (res) {
                    setReportData(res.data || []);
                    setTotalRecords(res.total || 0);
                    setTotalPages(res.last_page || 1);
                }
            })
            .catch(err => console.error('Failed to fetch LPO report:', err))
            .finally(() => setLoading(false));
    };

    // Effect Hooks for Fetching
    // Unified Effect Hook for Fetching
    useEffect(() => {
        const isSearchChanged = prevSearch.current !== searchTerm;
        prevSearch.current = searchTerm;

        if (isSearchChanged && currentPage !== 1) {
            setCurrentPage(1);
            return;
        }

        const delay = isSearchChanged ? 500 : 0;
        const timer = setTimeout(() => {
            fetchReport();
        }, delay);
        return () => clearTimeout(timer);
    }, [branchId, vehicleId, currentPage, perPage, sortBy, sortOrder, searchTerm, startDate, endDate]);

    const handlePageChange = (newPage: number) => setCurrentPage(newPage);

    const handlePerRowsChange = (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSort = (column: any, sortDirection: string) => {
        setSortBy(column.sortField || 'lpo_date');
        setSortOrder(sortDirection);
    };

    // PDF Download
    const handleDownload = async () => {
        setLoading(true);
        try {
            const payload: any = {
                page: 1,
                per_page: totalRecords > 0 ? totalRecords : 10000,
                sort_by: sortBy,
                sort_order: sortOrder,
                search: searchTerm,
                from_date: startDate ? formatDate(startDate) : '',
                to_date: endDate ? formatDate(endDate) : ''
            };
            if (branchId) payload.branch_id = branchId;
            if (vehicleId) payload.vehicle_id = vehicleId;

            const res: any = await apiPost("reports/lpo", payload);
            const allData = res.data || [];

            const doc = new jsPDF('l', 'mm', 'a4') as any; // Landscape for more columns
            doc.text("LPO Report", 14, 20);
            doc.setFontSize(10);
            doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 26);
            if (startDate && endDate) {
                doc.text(`Period: ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`, 14, 32);
            }

            const tableRows = allData.map((item: LPORecord, index: number) => {
                const vendorDisplay = (() => {
                    const type = item.lpo_type;
                    const pVendor = item.vendor_name;
                    const lVendor = item.labour_vendor_name || item.vendor_name;

                    if (type === 'Same Hybrid') return `Parts & Labour: ${pVendor}`;
                    if (type === 'Diff Hybrid') return `Parts: ${pVendor}, Labour: ${lVendor}`;
                    if (type === 'Labour') return `Labour: ${lVendor}`;
                    return `Parts: ${pVendor}`;
                })();

                return [
                    index + 1,
                    item.lpo_number || '-',
                    item.branch_name || '-',
                    item.registration_number || '-',
                    item.lpo_date ? new Date(item.lpo_date).toLocaleDateString('en-GB') : '-',
                    vendorDisplay,
                    item.amount ? Number(item.amount).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'
                ];
            });

            autoTable(doc, {
                startY: startDate && endDate ? 40 : 35,
                head: [['Sl.No', 'LPO No.', 'Branch', 'Vehicle', 'Date', 'Vendor', `Amount (${config.currency})`]],
                body: tableRows,
                styles: { fontSize: 9 },
                headStyles: { fillColor: [0, 150, 136] }
            });

            doc.save('LPO_Report.pdf');
        } catch (error) {
            console.error('PDF generation failed:', error);
        } finally {
            setLoading(false);
        }
    };

    // Print Handler
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        onBeforeGetContent: async () => {
            setLoading(true);
            try {
                const payload: any = {
                    page: 1,
                    per_page: totalRecords > 0 ? totalRecords : 10000,
                    sort_by: sortBy,
                    sort_order: sortOrder,
                    search: searchTerm,
                    from_date: startDate ? formatDate(startDate) : '',
                    to_date: endDate ? formatDate(endDate) : ''
                };
                if (branchId) payload.branch_id = branchId;
                if (vehicleId) payload.vehicle_id = vehicleId;

                const res: any = await apiPost("reports/lpo", payload);
                setPrintData(res.data || []);
                return new Promise((resolve) => setTimeout(resolve, 500));
            } catch (error) {
                console.error('Print data fetch failed:', error);
                return Promise.reject(error);
            } finally {
                setLoading(false);
            }
        },
    });

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: { fontSize: '14px', fontWeight: '600' },
        },
        rows: {
            style: {
                minHeight: '50px',
                '&:not(:last-of-type)': {
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e0e0e0',
                },
            },
        },
    };

    if (!hasPermission('lpo-report.view')) {
        return (
            <div className="page-body">
                <Breadcrumbs parent="Reports" title="LPO Report" pageTitle="LPO Report" />
                <div className="container-fluid">
                    <Card className="border-0 shadow-sm">
                        <CardBody className="text-center py-5">
                            <p className="text-muted mb-0">You don't have permission to view this report.</p>
                        </CardBody>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="page-body">
            <Breadcrumbs parent="Reports" title="LPO Report" pageTitle="LPO Report" />
            <div className="container-fluid">
                <Row className="d-print-none mb-3">
                    <Col md={4}>
                        <Card className="h-80 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #e0f7fa 0%, #ffffff 100%)', borderLeft: '4px solid #009688' }}>
                            <CardBody className="py-3 d-flex align-items-center justify-content-between">
                                <div>
                                    <h6 className="text-muted text-uppercase small fw-bold mb-2">Total Records</h6>
                                    <h4 className="text-dark mb-0 fw-bold">{totalRecords}</h4>
                                </div>
                                <div className="p-3 bg-white rounded-circle shadow-sm">
                                    <FileText size={24} style={{ color: '#009688' }} />
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={4}></Col>
                    <Col md={4} className="bg-transparent border-0 d-flex justify-content-end align-items-center px-0 flex-wrap gap-2">
                        {hasPermission('lpo-report.download') && (
                            <Button color="success" className="px-4 py-2 rounded-pill d-flex align-items-center gap-2 shadow-sm" onClick={handleDownload} disabled={loading}>
                                <Download size={16} /> <span className="fw-bold">Download</span>
                            </Button>
                        )}
                        {hasPermission('lpo-report.print') && (
                            <Button color="primary" className="px-4 py-2 rounded-pill d-flex align-items-center gap-2 shadow-sm" onClick={handlePrint} disabled={loading}>
                                <Printer size={16} /> <span className="fw-bold">Print</span>
                            </Button>
                        )}
                    </Col>
                </Row>

                {/* Filters */}
                <Card className="border-0 shadow-sm mb-4 bg-light">
                    <CardBody className="p-4">
                        <Row className="g-3">
                            <Col md={3}>
                                <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1" style={{ color: '#009688' }}>
                                    <MapPin size={14} style={{ color: '#009688' }} /> Select Branch
                                </Label>
                                <Input
                                    type="select"
                                    value={branchId}
                                    onChange={(e) => setBranchId(e.target.value)}
                                    bsSize="sm"
                                    className="form-control"
                                    style={{ borderRadius: '8px', border: '1px solid #ced4da', padding: '0.5rem' }}
                                >
                                    <option value="">All Branches</option>
                                    {branches.map((b) => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
                                </Input>
                            </Col>
                            <Col md={3}>
                                <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1" style={{ color: '#009688' }}>
                                    <Truck size={14} style={{ color: '#009688' }} /> Filter Vehicle
                                </Label>
                                <Input
                                    type="select"
                                    value={vehicleId}
                                    onChange={(e) => setVehicleId(e.target.value)}
                                    bsSize="sm"
                                    className="form-control"
                                    style={{ borderRadius: '8px', border: '1px solid #ced4da', padding: '0.5rem' }}
                                    disabled={!branchId}
                                >
                                    <option value="">All Vehicles</option>
                                    {vehicles.map((v) => <option key={v.id} value={v.id}>{v.registration_number}</option>)}
                                </Input>
                            </Col>
                            <Col md={2}>
                                <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1" style={{ color: '#009688' }}>
                                    <Calendar size={14} style={{ color: '#009688' }} /> From Date
                                </Label>
                                <div className="rounded overflow-hidden">
                                    <DatePicker
                                        className="form-control"
                                        selected={startDate}
                                        onChange={(date) => { setStartDate(date); setCurrentPage(1); }}
                                        dateFormat="dd/MM/yyyy"
                                        placeholderText="Start Date"
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                    />
                                </div>
                            </Col>
                            <Col md={2}>
                                <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1" style={{ color: '#009688' }}>
                                    <Calendar size={14} style={{ color: '#009688' }} /> To Date
                                </Label>
                                <div className="rounded overflow-hidden">
                                    <DatePicker
                                        className="form-control"
                                        selected={endDate}
                                        onChange={(date) => { setEndDate(date); setCurrentPage(1); }}
                                        dateFormat="dd/MM/yyyy"
                                        placeholderText="End Date"
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                    />
                                </div>
                            </Col>
                            <Col md={2}>
                                <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1" style={{ color: '#009688' }}>
                                    <Search size={14} style={{ color: '#009688' }} /> Search
                                </Label>
                                <Input
                                    type="text"
                                    placeholder="Search ..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    bsSize="sm"
                                    className="form-control"
                                    style={{ borderRadius: '8px', border: '1px solid #ced4da', padding: '0.5rem', height: '35px' }}
                                />
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Table */}
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}>
                        <Spinner color="primary" />
                    </div>
                ) : (
                    <Card className="border-0 shadow-sm">
                        <CardBody className="p-0">
                            <div className="rounded-3 overflow-hidden">
                                <DataTable
                                    columns={columns}
                                    data={reportData}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={totalRecords}
                                    paginationDefaultPage={currentPage}
                                    onChangePage={handlePageChange}
                                    onChangeRowsPerPage={handlePerRowsChange}
                                    sortServer
                                    onSort={handleSort}
                                    highlightOnHover
                                    responsive
                                    customStyles={customStyles}
                                    noDataComponent={
                                        <div className="py-5 text-center text-muted">
                                            <p className="mb-0">No approved LPOs found</p>
                                        </div>
                                    }
                                />
                            </div>
                        </CardBody>
                    </Card>
                )}

                {/* Print Component (Hidden) */}
                <div style={{ display: 'none' }}>
                    <div ref={printRef} style={{ padding: '30px', fontFamily: 'Arial, sans-serif' }}>
                        <div style={{ marginBottom: '20px' }}>
                            <h2 style={{ fontSize: '18px', margin: '0 0 5px 0', fontWeight: 'bold' }}>LPO Report</h2>
                            <p style={{ fontSize: '10px', color: '#666', margin: 0 }}>Generated: {new Date().toLocaleDateString()}</p>
                            {startDate && endDate && (
                                <p style={{ fontSize: '10px', color: '#666', margin: '5px 0 0 0' }}>Period: {startDate.toLocaleDateString()} to {endDate.toLocaleDateString()}</p>
                            )}
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px', tableLayout: 'auto' }}>
                            <thead style={{ display: 'table-header-group' }}>
                                <tr style={{ backgroundColor: '#009688', color: '#fff' }}>
                                    <th style={{ padding: '6px', textAlign: 'center', fontWeight: 'bold', border: '1px solid #ddd' }}>Sl.No</th>
                                    <th style={{ padding: '6px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #ddd' }}>LPO No.</th>
                                    <th style={{ padding: '6px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #ddd' }}>Branch</th>
                                    <th style={{ padding: '6px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #ddd' }}>Vehicle</th>
                                    <th style={{ padding: '6px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #ddd' }}>Date</th>
                                    <th style={{ padding: '6px', textAlign: 'left', fontWeight: 'bold', border: '1px solid #ddd' }}>Vendor</th>
                                    <th style={{ padding: '6px', textAlign: 'right', fontWeight: 'bold', border: '1px solid #ddd' }}>Amount ({config.currency})</th>
                                </tr>
                            </thead>
                            <tbody>
                                {printData.map((item, index) => {
                                    const vendorDisplay = (() => {
                                        const type = item.lpo_type;
                                        const pVendor = item.vendor_name;
                                        const lVendor = item.labour_vendor_name || item.vendor_name;

                                        if (type === 'Same Hybrid') return `Parts & Labour: ${pVendor}`;
                                        if (type === 'Diff Hybrid') return `Parts: ${pVendor}, Labour: ${lVendor}`;
                                        if (type === 'Labour') return `Labour: ${lVendor}`;
                                        return `Parts: ${pVendor}`;
                                    })();

                                    return (
                                        <tr key={index} style={{ pageBreakInside: 'avoid' }}>
                                            <td style={{ padding: '6px', textAlign: 'center', border: '1px solid #ddd' }}>{index + 1}</td>
                                            <td style={{ padding: '6px', border: '1px solid #ddd' }}>{item.lpo_number || '-'}</td>
                                            <td style={{ padding: '6px', border: '1px solid #ddd' }}>{item.branch_name || '-'}</td>
                                            <td style={{ padding: '6px', border: '1px solid #ddd' }}>{item.registration_number || '-'}</td>
                                            <td style={{ padding: '6px', border: '1px solid #ddd' }}>{item.lpo_date ? new Date(item.lpo_date).toLocaleDateString('en-GB') : '-'}</td>
                                            <td style={{ padding: '6px', border: '1px solid #ddd' }}>{vendorDisplay}</td>
                                            <td style={{ padding: '6px', textAlign: 'right', border: '1px solid #ddd', fontFamily: 'monospace' }}>
                                                {item.amount ? Number(item.amount).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <div style={{ marginTop: '20px', paddingTop: '10px', borderTop: '1px solid #eee', textAlign: 'center', color: '#999', fontSize: '10px' }}>
                            Fleet Management System © {new Date().getFullYear()}
                        </div>
                    </div>
                </div>
            </div>
            <style>{`@media print { .d-print-none { display: none!important; } .d-print-block { display: block!important; } .page-body { padding: 0; margin: 0; } .card { border: none!important; box-shadow: none!important; } }`}</style>
        </div>
    );
};

export default LPOReport;